import { useState } from "react";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import QueryView from "../../common/lib/QueryView";
import SpinnerButton from "../../common/SpinnerButton";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import useGetSiteSettings from "../../requests/useGetSiteSettings";
import useUpdateCustomerDestinationSettings, { DestinationOptions } from "../../requests/useUpdateCustomerDestinationSettings";

interface CheckboxState {
  showDestination: boolean;
  allowChange: boolean;
  requireDestination: boolean;
  allowShipTo: boolean;
  requireCityState: boolean;
}

const toCheckboxState = (setting: DestinationOptions): CheckboxState => ({
  showDestination: setting !== DestinationOptions.None,
  allowChange: setting !== DestinationOptions.None && setting !== DestinationOptions.ShowDestination,
  requireDestination: setting === DestinationOptions.AllowDestinationChangeAndRequireDestination ||
    setting === DestinationOptions.AllowEnteredShipToAndRequireDestination ||
    setting === DestinationOptions.AllowEnteredCityStateAndRequireDestination,
  allowShipTo: setting === DestinationOptions.AllowEnteredShipTo ||
    setting === DestinationOptions.AllowEnteredShipToAndRequireDestination ||
    setting === DestinationOptions.AllowEnteredCityState ||
    setting === DestinationOptions.AllowEnteredCityStateAndRequireDestination,
  requireCityState: setting === DestinationOptions.AllowEnteredCityState ||
    setting === DestinationOptions.AllowEnteredCityStateAndRequireDestination,
});

const toDestinationOption = (state: CheckboxState): DestinationOptions => {
  if (!state.showDestination) return DestinationOptions.None;
  if (!state.allowChange) return DestinationOptions.ShowDestination;
  if (state.requireCityState && state.requireDestination) return DestinationOptions.AllowEnteredCityStateAndRequireDestination;
  if (state.requireCityState) return DestinationOptions.AllowEnteredCityState;
  if (state.allowShipTo && state.requireDestination) return DestinationOptions.AllowEnteredShipToAndRequireDestination;
  if (state.allowShipTo) return DestinationOptions.AllowEnteredShipTo;
  if (state.requireDestination) return DestinationOptions.AllowDestinationChangeAndRequireDestination;
  return DestinationOptions.AllowDestinationChange;
};

const CustomerDestination = () => {
  const displayMessage = useDisplayMessage();

  const useGetCustomerDestinationSettings = useGetSiteSettings<{ destinationSetting: DestinationOptions }>({
    onError: (err) => displayMessage.fail(adminFacingErrorMessage(err))
  });

  return <QueryView
    query={useGetCustomerDestinationSettings}
    renderData={({ destinationSetting }) => <>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <CustomerDestinationForm
        initialSetting={destinationSetting} />
    </>
    }
  />
}

interface CheckboxProps {
  id: string,
  onChange: (checked: boolean) => void,
  checked: boolean,
  disabled?: boolean,
  text: string,
}

const Checkbox = (props: CheckboxProps) => {
  return <div className="form-check">
    <input
      {...props}
      type="checkbox"
      className="form-check-input"
      onChange={ev => props.onChange(ev.target.checked)} />
    <label className="form-check-label" htmlFor={props.id} >{props.text}</label>
  </div>;
}

interface DestinationFormProps {
  initialSetting: DestinationOptions,
}

const CustomerDestinationForm = ({ initialSetting }: DestinationFormProps) => {
  const [state, setState] = useState(() => toCheckboxState(initialSetting));
  const displayMessage = useDisplayMessage();

  const updateCustomerDestinationSettings = useUpdateCustomerDestinationSettings({
    onSuccess: () => displayMessage.success("Customer Destination settings updated"),
    onError: (err) => displayMessage.fail(adminFacingErrorMessage(err))
  });

  const setShowDestination = (checked: boolean) =>
    setState({ showDestination: checked, allowChange: false, requireDestination: false, allowShipTo: false, requireCityState: false });

  const setAllowChange = (checked: boolean) =>
    setState(s => ({ ...s, allowChange: checked, requireDestination: false, allowShipTo: false, requireCityState: false }));

  const setRequireDestination = (checked: boolean) =>
    setState(s => ({ ...s, requireDestination: checked }));

  const setAllowShipTo = (checked: boolean) =>
    setState(s => ({ ...s, allowShipTo: checked, requireCityState: false }));

  const setRequireCityState = (checked: boolean) =>
    setState(s => ({ ...s, requireCityState: checked }));

  return <>
    {displayMessage?.message != null && <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />}
    <form className="m-auto w-50">
      <h2>Customer Destination</h2>
      <div className="border border-dark rounded">
        <div className="m-3">
          <Checkbox
            id="ShowCustomerDestination"
            onChange={setShowDestination}
            checked={state.showDestination}
            text="Show Customer Destination"
          />
          <Checkbox
            id="AllowDestinationChange"
            onChange={setAllowChange}
            checked={state.allowChange}
            disabled={!state.showDestination}
            text="Allow drivers to select a Customer Destination"
          />
          <div className="ms-5">
            <Checkbox
              id="RequireDestination"
              onChange={setRequireDestination}
              checked={state.requireDestination}
              disabled={!state.allowChange}
              text="Require drivers to select a Customer Destination"
            />
            <Checkbox
              id="AllowEnteredShip"
              onChange={setAllowShipTo}
              checked={state.allowShipTo}
              disabled={!state.allowChange}
              text="Allow drivers to enter a new Customer Destination"
            />
            <div className="ms-5">
              <Checkbox
                id="RequireCityState"
                onChange={setRequireCityState}
                checked={state.requireCityState}
                disabled={!state.allowShipTo}
                text="Require drivers to enter City and State"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
    <div className="align-self-end">
      <SpinnerButton
        className="btn btn-primary mx-4 mb-4 px-5 py-3"
        spinning={updateCustomerDestinationSettings.isLoading}
        onClick={() => updateCustomerDestinationSettings.request({ setting: toDestinationOption(state) })}>
        Save
      </SpinnerButton>
    </div>
  </>
}

export { CustomerDestination };
